import { helpers as helpersLegacy } from '@sword-health/vuelidate/lib/validators'
import {
  required,
  minLength,
  maxLength,
  numeric,
  helpers,
} from '@vuelidate/validators'

import * as z from 'zod'
import { VALIDATION_TYPES } from './zod-keys'

/**
 * zodToVuelidate
 * ---------------
 * Converts a Zod schema into the validators format Vuelidate expects.
 *
 * • Field-level rules — required, length, regex, email, numeric…
 * • Schema-level .refine() for password confirmation
 */
export default function zodToVuelidate(schema) {
  // Unwrap top-level effects
  const baseSchema = schema instanceof z.ZodEffects ? schema._def.schema : schema
  const shape = baseSchema instanceof z.ZodObject ? baseSchema.shape : {}
  const validations = {}

  // Field-level rules
  for (const key in shape) {
    let fieldSchema = shape[key]
    const refinements = []
    const rules = []

    const isOptionalField = typeof fieldSchema.isOptional === 'function' && fieldSchema.isOptional()

    // Unwrap field-level effects (e.g., .refine().describe())
    if (fieldSchema instanceof z.ZodEffects) {
      const description = fieldSchema._def.description || {}

      refinements.push({
        type: typeof description === 'string' ? description : description.type || VALIDATION_TYPES.generic,
        typeParams: description.params || {},
        refineFn: fieldSchema._def.effect.refinement,
      })
      fieldSchema = fieldSchema._def.schema
    }

    // Unwrap optionals
    if (fieldSchema instanceof z.ZodOptional || fieldSchema instanceof z.ZodDefault) {
      fieldSchema = fieldSchema._def.innerType
    }

    if (!isOptionalField) rules.push(required)

    // Validation for nested objects
    if (fieldSchema instanceof z.ZodObject) {
      validations[key] = zodToVuelidate(fieldSchema)
      continue
    }

    if (fieldSchema instanceof z.ZodString) {
      // Handle regex-based validations from schema
      const regexDef = fieldSchema._def.checks.find((check) => check.kind === 'regex')

      if (regexDef) {
        // Check if field has a description type compatible with vuelidate type
        const descriptionType = fieldSchema._def.description?.type
        const validatorType = (descriptionType && VALIDATION_TYPES[descriptionType])
          ? VALIDATION_TYPES[descriptionType]
          : VALIDATION_TYPES.invalidCharacter

        rules.push(helpers.withParams({ type: validatorType }, helpers.regex(regexDef.regex)))
      }

      // Handle specific string validations
      if (fieldSchema.minLength) rules.push(minLength(fieldSchema.minLength))
      if (fieldSchema.maxLength) rules.push(maxLength(fieldSchema.maxLength))
    }

    if (fieldSchema instanceof z.ZodNumber) rules.push(numeric)

    if (fieldSchema instanceof z.ZodArray) {
      // Handle array fields validation
      if (fieldSchema._def.minLength) {
        rules.push(helpers.withParams(
          { type: VALIDATION_TYPES.minLength, min: fieldSchema._def.minLength.value },
          (arr) => Array.isArray(arr) && arr.length >= fieldSchema._def.minLength.value,
        ))
      }
      if (fieldSchema._def.maxLength) {
        rules.push(helpers.withParams(
          { type: VALIDATION_TYPES.maxLength, max: fieldSchema._def.maxLength.value },
          (arr) => Array.isArray(arr) && arr.length <= fieldSchema._def.maxLength.value,
        ))
      }
    }

    // Convert each refine function found into a Vuelidate validator. 'Describe' name of the refine rule would be the validator name/type
    refinements.forEach((refinement) => {
      rules.push(helpers.withParams({ type: refinement.type, ...refinement.typeParams }, (value) => {
        return refinement.refineFn(value, { addIssue: () => true })
      }))
    })

    // Aggregate under the field key
    validations[key] = rules.reduce((acc, rule) => {
      const { type } = rule.$params

      const validator = { [type]: (val) => helpersLegacy.withParams(rule.$params, rule.$validator)(val) }

      return { ...acc, ...validator }
    }, {})
  }

  // Schema-level .refine() for password confirmation
  if (schema instanceof z.ZodEffects &&
     schema._def.effect.type === 'refinement' &&
     schema._def.description?.type === VALIDATION_TYPES.custom.passwordMatch) {
    const effect = schema._def.effect
    let target = []

    // Probe once to get issue.path
    try {
      let toggle = false
      const triggerData = Object.fromEntries(
        Object.keys(shape).map((k) => {
          toggle = !toggle

          return [k, toggle ? 'A' : 'B'] // ensures mismatch
        }),
      )

      effect.refinement(triggerData, {
        addIssue: (issue) => {
          if (Array.isArray(issue.path) && issue.path.length) target = issue.path
        },
      })
    } catch {/* ignore */}

    if (target.length === 0) target = Object.keys(shape)

    // Add a validation for each field in the target
    target.forEach((field) => {
      validations[field] = validations[field] || {}
      validations[field].sameAsPassword = (value, vm) =>
        helpersLegacy.withParams({ type: 'sameAsPassword' }, () => {
          const data = Object.fromEntries(Object.keys(shape).map((k) => [k, vm[k]]))

          try {
            return effect.refinement(data, { addIssue: () => false })
          } catch {
            return false
          }
        })(value)
    })
  }

  return validations
}
